// ── Admin map-object persistence ──
// The two backend writes that end an admin gesture: a resize (new display size) and a
// move (new world position). Both follow the same contract — write through to the
// `mapObjects` query cache on success, put the PIXI object back where it was and alert
// the user on failure — so they live together.
//
// Extracted verbatim from useMapAdminInteraction's handlePointerUp; the only change is
// closure variables → explicit params.
import * as PIXI from 'pixi.js';
import type { QueryClient } from '@tanstack/react-query';
import { MapObject } from '../../../models/mapObject';
import { adminUpdateMapObjectCoordinatesAdminMapObjectsMapObjectIdCoordinatesPut } from '../../../client';
import { showAlert } from '../../../utils/telegram/alert';

export interface AdminObjectResizeSave {
  mapObjectId: number;
  marker: PIXI.Container;
  sprite: PIXI.Sprite | undefined;
  size: { width: number; height: number };
  /** Sprite size restored when the write fails. */
  rollbackSize: { width: number; height: number };
  queryClient: QueryClient;
}

export interface AdminObjectMoveSave {
  mapObjectId: number;
  marker: PIXI.Container;
  position: { x: number; y: number };
  /** Marker position restored when the write fails. */
  rollbackPosition: { x: number; y: number };
  queryClient: QueryClient;
}

/** Saves the sprite's new display size. Position is sent along, unchanged, by the same endpoint. */
export async function saveAdminObjectResize(save: AdminObjectResizeSave): Promise<void> {
  const { mapObjectId, marker, sprite, size, rollbackSize, queryClient } = save;
  try {
    await adminUpdateMapObjectCoordinatesAdminMapObjectsMapObjectIdCoordinatesPut({
      path: { map_object_id: mapObjectId },
      body: {
        x: marker.x,
        y: marker.y,
        display_width: size.width,
        display_height: size.height,
      },
      throwOnError: true,
    });

    queryClient.setQueryData<MapObject[]>(['mapObjects'], (oldData = []) => {
      return oldData.map(obj =>
        obj.id === mapObjectId
          ? { ...obj, display_width: size.width, display_height: size.height }
          : obj
      );
    });
  } catch (error) {
    console.error('[MapCanvas] Error saving resize:', error);
    // Revert sprite size
    if (sprite && !sprite.destroyed && !marker.destroyed) {
      sprite.width = rollbackSize.width;
      sprite.height = rollbackSize.height;
    }
    showAlert('Помилка при збереженні розміру об\'єкта');
  }
}

/** Applies the dropped position to the marker, then persists it. */
export async function saveAdminObjectMove(save: AdminObjectMoveSave): Promise<void> {
  const { mapObjectId, marker, position, rollbackPosition, queryClient } = save;

  marker.position.set(position.x, position.y);
  marker.zIndex = position.y;

  try {
    await adminUpdateMapObjectCoordinatesAdminMapObjectsMapObjectIdCoordinatesPut({
      path: { map_object_id: mapObjectId },
      body: {
        x: position.x,
        y: position.y,
      },
      throwOnError: true,
    });

    queryClient.setQueryData<MapObject[]>(['mapObjects'], (oldData = []) => {
      return oldData.map(obj =>
        obj.id === mapObjectId
          ? { ...obj, x: position.x, y: position.y }
          : obj
      );
    });
  } catch (error) {
    console.error('[MapCanvas] Error updating map object coordinates:', error);
    if (!marker.destroyed) {
      marker.position.set(rollbackPosition.x, rollbackPosition.y);
      marker.zIndex = rollbackPosition.y;
    }
    showAlert('Помилка при оновленні позиції об\'єкта');
  }
}
